/** Imports */
import {z} from 'zod';
import {zUser} from '@/mongoSchemas/User/Users.zod';
import {zMedia} from '@/mongoSchemas/Post/Medias.zod';
import {zMongoObject, zMongoObjectId} from '@/types/utils';
import {zDashboard} from '@/mongoSchemas/Organization/Dashboards.zod';
import {
    zTimezones,
    zOrganizationCountryCodeSchema,
    zOrganizationLanguageCodeSchema,
} from '@/mongoSchemas/Locale/LanguageCode.zod';


/** Types */
export type PaymentPlanType = z.infer<typeof zPaymentPlan>;
export type PaymentPlanStatus = z.infer<typeof zOrganization.shape.planStatus>;
export type PostingPreset = z.infer<typeof zPostingPreset>;
export type MemberRole = z.infer<typeof zMemberRole>;
export type Timezone = z.infer<typeof zOrganization.shape.timezone>;
export type OrganizationWeekStartsOn = z.infer<typeof zOrganizationWeekStartsOn>;
export type Member = z.infer<typeof zOrganization.shape.members.element>;
export type Addon = z.infer<typeof zAddon>;
export type Organization = z.infer<typeof zOrganization>;
export type ClientAddon = z.infer<typeof zClientAddon>;
export type AiTokens = z.infer<typeof zAiTokens>;
export type OrganizationCountryCode = z.infer<typeof zOrganizationCountryCodeSchema>;
export type OrganizationLanguageCode = z.infer<typeof zOrganizationLanguageCodeSchema>;


/** Constants */
export const DEFAULT_POSTING_COLLISION_SEARCH_RADIUS_MINUTES = 60;
export const MIN_POSTING_COLLISION_SEARCH_RADIUS_MINUTES = 0;
export const MAX_POSTING_COLLISION_SEARCH_RADIUS_MINUTES = 1440;


/// Zod schemas
export const zMemberRole = z.enum([
    'owner',
    'approver',
    'user:canEdit',
    'user:canView',
    'pages:canEdit',
    'pages:canView',
    'posts:canEdit',
    'posts:canView',
    'reportings:canEdit',
    'reportings:canView',
    'foxlink:shortLinks:canEdit',
    'foxlink:shortLinks:canView',
    'foxlink:linkTrees:canEdit',
    'foxlink:linkTrees:canView',
]);
const zAddonId = z.enum([
    'amazon-aws-s3',
    'google-drive',
    'discord-bot',
    'short-links',
    'bulk-scheduler',
    'comments-central',
    'media-library',
]);
export const zPaymentPlan = z.enum([
    'NONE',
    'PROFESSIONAL',
    'TEAM',
    'ENTERPRISE',
]);
export const zOrganizationWeekStartsOn = z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]);
export const zClientAddon = z.object({
    id: zAddonId,
    label: z.string(),
    description: z.string(),
    image: z.string(),
    paymentPlan: z.union([
        zPaymentPlan,
        z.literal('ALL'),
    ]).array(),
    config: z.any().optional(),
    addedAt: z.instanceof(Date).optional(),
    updatedAt: z.instanceof(Date).optional(),
});
export const zAddon = z.object({
    id: zAddonId,
    config: z.any(),
    addedAt: z.instanceof(Date),
    updatedAt: z.instanceof(Date),
});
export const zPostingPreset = z.object({
    _id: zMongoObjectId,
    name: z.string(),
    value: z.string(),
});
export const zAiTokens = z.object({
    usedTotal: z.number().optional().default(0),
    usedLast30Days: z.number().optional().default(0),
    lastResetAt: z.instanceof(Date).optional().default(() => new Date()),
    availableTokens: z.number().optional().default(-1),
});
export const zOrganization = zMongoObject.extend({

    // Meta
    name: z.string(),
    picture: zMedia.nullish(),
    creator: z.union([
        zMongoObjectId,
        zUser,
    ]),
    members: z.object({
        user: z.union([
            zMongoObjectId,
            zUser,
        ]),
        roles: zMemberRole.array(),
        invitedBy: z.union([
            zMongoObjectId,
            zUser,
        ]),
        invitedAt: z.instanceof(Date),
        inviteAccepted: z.boolean(),
        acceptedAt: z.instanceof(Date).optional(),
        inviteExpiresAt: z.instanceof(Date).optional(),
    }).array(),

    // Flags
    isAdminOrganization: z.boolean().default(false),
    deletedFlagSince: z.instanceof(Date).optional(),
    deletedFlagToken: z.instanceof(Date).optional(),

    // Subscription
    paymentPlan: zPaymentPlan,
    trialEndsAt: z.instanceof(Date).optional(),
    planStartedAt: z.instanceof(Date).optional(),
    stripeCustomerId: z.string().optional(),
    planStatus: z.enum([
        'TRIAL',
        'RUNNING',
        'CANCELED',
        'ENDED',
    ]).optional(),
    hasSpecialPlan: z.boolean().optional(),

    // Presets
    presetsTitle: zPostingPreset.array().optional(),
    presetsCaption: zPostingPreset.array().optional(),
    presetsTags: zPostingPreset.array().optional(),

    // Settings
    timezone: zTimezones.optional(),
    weekStartsOn: zOrganizationWeekStartsOn.optional().default('monday'),
    addons: zAddon.array(),
    filteredWords: z.string().array().optional(),
    useDefaultWordFilter: z.boolean().optional(),
    country: zOrganizationCountryCodeSchema.optional(),
    language: zOrganizationLanguageCodeSchema.optional(),
    allowedPages: z.string().array().optional(),
    postingCollisionSearchRadiusMinutes: z.number()
        .int()
        .min(MIN_POSTING_COLLISION_SEARCH_RADIUS_MINUTES)
        .max(MAX_POSTING_COLLISION_SEARCH_RADIUS_MINUTES)
        .optional()
        .default(DEFAULT_POSTING_COLLISION_SEARCH_RADIUS_MINUTES),

    // AI Tokens
    aiTokens: zAiTokens.optional(),

    // Quick view dashboard
    get quickViewDashboard() {
        return z.union([
            zMongoObjectId,
            zDashboard,
        ]).optional();
    },
});
